import React from 'react';
import { Star } from '@styled-icons/material-outlined/Star';
import { Text, TextVariant } from '@naf/text';
import { useAppInsightsContext, useTrackMetric } from '@microsoft/applicationinsights-react-js';
import { useLocation } from 'react-router';
import { AuthorBio } from '@naf-simba/sections-authorbio';
import { InformationSummary } from '@naf-simba/sections-information-summary';
import TravelMapV2 from '@components/google-maps/TravelMapV2';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import useSelector from '@redux/typedHooks';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import BlockContent from '@components/block-content/BlockContent';
import RelatedArticleList from '@components/related-articles/RelatedArticleList';
import RelatedLinksList from '@components/related-links/RelatedLinksList';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { LayoutWithMainContent } from '@components/layout/Layout';
import SchemaArticle from '@components/structured-data/SchemaArticle/SchemaArticle';
import { ArticleType } from '@appTypes/articleType';
import TableOfContents from '@components/TableOfContents/TableOfContents';
import { ArticleHeader } from '@components/ArticleHeader/ArticleHeader';
import ArticleSeriesHeader from '@components/articleSeries/ArticleSeriesHeader/ArticleSeriesHeader';
import ArticleSeriesFooter from '@components/articleSeries/ArticleSeriesFooter/ArticleSeriesFooter';
import { MetaData } from '@components/metaData/MetaData';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { BlockContentContext } from '@components/block-content/BlockContent.utils';
import ArticleTags from '../ArticleComponents/ArticleTags/ArticleTags';
import { LoaderContent } from '../LoaderPage';
import { useArticleScrollTracking } from './useArticleScrollTracking';
import {
  getArticleCategoryData,
  getArticleSEOData,
  getArticleSchemaData,
  getArticleGTMArgs,
  getAuthorBioTitle,
  openGraphImageWidth,
} from './Article.utils';
import * as S from './Styles';

interface Props {
  article: ArticleType;
}

const Article = ({ article }: Props) => {
  const appInsights = useAppInsightsContext();
  const windowPath = useWindowLocation();
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  useTrackMetric(appInsights, 'Artikkel');

  const {
    name,
    runningHead,
    ingress,
    body,
    id,
    image,
    relatedContent,
    video,
    meta,
    seoConfig,
    tags,
    articleScore,
    relatedLinks,
    category,
    slug,
    metaDisplay,
    type,
    userAccessibility,
    tableOfContents,
    displayFeatureHeader,
    articleSeries,
    openGraphImage,
    routePoints,
    travelMode,
    youtubeVideo,
    summary,
  } = article;
  const { getProcessedImage } = useCloudinary();
  const ogImage = openGraphImage || image;
  const cldOpenGraphImage = getProcessedImage(ogImage?.publicId || ogImage?.public_id, {
    aspectRatio: '16:9',
    width: openGraphImageWidth,
    focusMethod: ogImage?.focus,
  })?.toURL();
  const { mappedCategories } = useSelector((state) => state.sitestructure);
  const isUpdatingArticle = useSelector((state) => state.documents.mappedData[slug]?.meta.isUpdating);
  const articleUrl = location && location.pathname ? location.pathname : `${slug}`;
  // Category data.
  const { mappedCategory, mappedParentCategory, pageCategory, isHiddenCategory } = getArticleCategoryData(
    article,
    mappedCategories,
  );
  // SEO data.
  const { seoDescription, seoTitle } = getArticleSEOData(article);
  // Structured data.
  const { schemaType, schemaArticleSection, schemaAuthor, schemaUrl } = getArticleSchemaData(
    article,
    mappedCategory,
    mappedParentCategory,
    articleUrl,
  );
  const gtmArgs = getArticleGTMArgs(article, pageCategory);
  const articleRef = useArticleScrollTracking({ articleId: id, dataLayer });

  return (
    <LayoutWithMainContent
      title={name}
      description={ingress}
      url={windowPath}
      imgUrl={cldOpenGraphImage}
      imgWidth={cldOpenGraphImage ? openGraphImageWidth : undefined}
      gtmArgs={gtmArgs}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoDescription={seoDescription}
      seoTitle={seoTitle}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Article,
        imagePublicId: image?.publicId,
        author: meta?.editor?.name,
        tags,
      }}
      backToTopButton
    >
      <S.Wrapper>
        <SchemaArticle
          id={slug}
          type={schemaType}
          title={seoTitle}
          description={seoDescription}
          imgUrl={cldOpenGraphImage}
          url={schemaUrl}
          datePublished={meta?.publishAt}
          dateModified={meta?.updatedAt}
          author={schemaAuthor}
          articleSection={schemaArticleSection}
          isAccessibleForFree={userAccessibility.viewType === 'Free'}
        />
        {!isHiddenCategory && !displayFeatureHeader && (
          <S.BreadCrumbsGrid>
            <S.BreadCrumbGridCol>
              <BreadCrumb />
            </S.BreadCrumbGridCol>
          </S.BreadCrumbsGrid>
        )}
        <ArticleHeader
          name={name}
          image={image || undefined}
          ingress={ingress}
          runningHead={runningHead}
          displayFeatureHeader={displayFeatureHeader || false}
          video={video || undefined}
          youtubeVideo={youtubeVideo}
          userAccessibility={userAccessibility}
          meta={meta}
          metaDisplay={metaDisplay}
          isHiddenCategory={isHiddenCategory}
          documentType={type}
        />
        {displayFeatureHeader && metaDisplay?.metaPosition !== 'bottom' && (
          <S.StyledArticleContentGrid>
            <S.ContentCol>
              <MetaData meta={meta} metaDisplay={metaDisplay} documentType={type} />
            </S.ContentCol>
          </S.StyledArticleContentGrid>
        )}
        {articleSeries && (
          <S.StyledArticleContentGrid>
            <ArticleSeriesHeader articleSeries={articleSeries} />
          </S.StyledArticleContentGrid>
        )}
        {summary?.title && (
          <S.StyledArticleContentGrid>
            <S.ContentCol>
              <S.InformationSummaryWrapper>
                <InformationSummary
                  title={summary.title}
                  content={summary.content && <BlockContent value={summary.content} />}
                  showDisclaimer={summary.showDisclaimer}
                />
              </S.InformationSummaryWrapper>
            </S.ContentCol>
          </S.StyledArticleContentGrid>
        )}
        {routePoints && <TravelMapV2 routePoints={routePoints} travelMode={travelMode} />}
        {tableOfContents?.enabled && body && (
          <S.StyledArticleContentGrid>
            <S.ContentCol>
              <TableOfContents tagType="h2" title={tableOfContents?.title} parentElement={articleRef} />
            </S.ContentCol>
          </S.StyledArticleContentGrid>
        )}
        {relatedLinks?.links?.length > 0 && (
          <S.StyledArticleContentGrid>
            <S.RelatedGridCol>
              <Text tag="h3" variant={TextVariant.Header3}>
                {relatedLinks.header ? relatedLinks.header : 'Les også'}
              </Text>
              <RelatedLinksList relatedLinks={relatedLinks.links} />
            </S.RelatedGridCol>
          </S.StyledArticleContentGrid>
        )}
        {!!articleScore?.selectedScore && (
          <S.StyledArticleContentGrid>
            <S.ContentCol>
              <S.ScoreWrapper>
                <Text tag="h3" variant={TextVariant.Header3}>
                  Testresultat
                </Text>
                <S.ScoreIconWrapper>
                  <Star size={24} />
                  <span>
                    <b>{articleScore.selectedScore} poeng</b> {!!articleScore.maxScore && `av ${articleScore.maxScore}`}
                  </span>
                </S.ScoreIconWrapper>
              </S.ScoreWrapper>
            </S.ContentCol>
          </S.StyledArticleContentGrid>
        )}
        {isUpdatingArticle && !body ? (
          <S.StyledArticleContentGrid>
            <S.ContentCol>
              <LoaderContent />
            </S.ContentCol>
          </S.StyledArticleContentGrid>
        ) : (
          (body && (
            <S.ArticleBlockWrapper ref={articleRef}>
              <BlockContent
                context={BlockContentContext.Article}
                value={body}
                isAccessibleForUser={userAccessibility.isAccessibleForUser}
              />
            </S.ArticleBlockWrapper>
          )) ||
          null
        )}
        {articleSeries && (
          <S.StyledArticleContentGrid>
            <ArticleSeriesFooter articleSeries={articleSeries} />
          </S.StyledArticleContentGrid>
        )}
        <S.StyledArticleContentGrid>
          <S.BottomContentCol>
            {metaDisplay?.showAuthorBio && meta?.editor?.bio && (
              <S.AuthorBioWrapper>
                <AuthorBio
                  title={getAuthorBioTitle(meta.editor.name, meta.editor.position)}
                  body={meta.editor.bio}
                  image={meta.editor.imageUrl ? { src: meta.editor.imageUrl, alt: meta.editor.name } : undefined}
                  link={
                    meta.editor.slug ? { text: 'Les mer', to: `/artikler?forfatter=${meta.editor.slug}` } : undefined
                  }
                />
              </S.AuthorBioWrapper>
            )}
            {metaDisplay?.metaPosition === 'bottom' && (
              <S.MetaDataWrapper>
                <MetaData meta={meta} metaDisplay={metaDisplay} documentType={type} />
              </S.MetaDataWrapper>
            )}
            {tags && (
              <ArticleTags
                tags={tags}
                mappedCategories={mappedCategories}
                category={category}
                articleUrl={articleUrl}
              />
            )}
          </S.BottomContentCol>
          {relatedContent?.length > 0 && (
            <S.RelatedGridCol>
              <S.ReadMoreHRLine />
              <Text tag="h2" variant={TextVariant.Header3} id="relatedContent">
                Les også
              </Text>
              <RelatedArticleList relatedContent={relatedContent} />
            </S.RelatedGridCol>
          )}
          {!isHiddenCategory && !metaDisplay?.hideForm && (
            <S.RelatedGridCol>
              <FeedbackSurvey contentUrl={windowPath} />
            </S.RelatedGridCol>
          )}
        </S.StyledArticleContentGrid>
      </S.Wrapper>
    </LayoutWithMainContent>
  );
};

export default Article;
